import { OrganizationAssignmentStateFilter, OrganizationAssignmentType } from '@today/types'
import { Badge, Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ProductConfirmDialogView } from '../product/product-confirm-dialog.view'
import { ErrorText, SelectInput, TextInput } from '../product/product-fields'
import { CheckboxInput, DateInput } from './org-fields'
import type {
	OrgAssignmentFormView,
	OrgAssignmentRowView,
	OrgConfirmState,
	OrgEditorState,
	OrgOption,
	Translate,
} from './org-types'

/**
 * Props for {@link OrgAssignmentsView}.
 *
 * @property userOptions - The tenant users for the picker; null without `user.read` (the dialog shows a UUID input).
 * @property onAssign / onEnd / onMakePrimary - Present only with `organization_assignment.write`.
 */
export interface OrgAssignmentsViewProps {
	t: Translate
	state: 'loading' | 'error' | 'ready'
	stateFilter: OrganizationAssignmentStateFilter
	rows: OrgAssignmentRowView[]
	editor: OrgEditorState<OrgAssignmentFormView> | null
	userOptions: OrgOption[] | null
	ending: OrgConfirmState | null
	rowError: string | null
	isBusy: boolean
	onStateFilterChange: (state: OrganizationAssignmentStateFilter) => void
	onRetry: () => void
	onEditorChange: <K extends keyof OrgAssignmentFormView>(field: K, value: OrgAssignmentFormView[K]) => void
	onEditorSubmit: () => void
	onEditorClose: () => void
	onConfirmEnd: () => void
	onCancelEnd: () => void
	onAssign?: () => void
	onEnd?: (id: string) => void
	onMakePrimary?: (id: string) => void
}

function UserField({
	props,
	editor,
}: {
	props: OrgAssignmentsViewProps
	editor: OrgEditorState<OrgAssignmentFormView>
}) {
	const { t } = props
	if (props.userOptions === null) {
		return (
			<TextInput
				label={t('assignments.fields.userId')}
				value={editor.values.userId}
				error={editor.errors.userId}
				onChange={(v) => props.onEditorChange('userId', v)}
			/>
		)
	}
	return (
		<SelectInput
			label={t('assignments.fields.user')}
			value={editor.values.userId}
			emptyLabel=""
			options={props.userOptions}
			error={editor.errors.userId}
			onChange={(v) => props.onEditorChange('userId', v)}
		/>
	)
}

function WindowFields({
	props,
	editor,
}: {
	props: OrgAssignmentsViewProps
	editor: OrgEditorState<OrgAssignmentFormView>
}) {
	const { t } = props
	return (
		<>
			<CheckboxInput
				label={t('assignments.fields.isPrimary')}
				checked={editor.values.isPrimary}
				onChange={(v) => props.onEditorChange('isPrimary', v)}
			/>
			<DateInput
				label={t('assignments.fields.validFrom')}
				value={editor.values.validFrom}
				onChange={(v) => props.onEditorChange('validFrom', v)}
			/>
			<DateInput
				label={t('assignments.fields.validTo')}
				value={editor.values.validTo}
				error={editor.errors.validTo}
				onChange={(v) => props.onEditorChange('validTo', v)}
			/>
		</>
	)
}

function AssignDialog({ props }: { props: OrgAssignmentsViewProps }): ReactElement | null {
	const { t, editor } = props
	if (!editor) return null
	return (
		<Dialog
			isOpen
			onOpenChange={(open) => (open ? undefined : props.onEditorClose())}
			title={t('assignments.dialogTitle')}
		>
			<form
				aria-label={t('assignments.dialogTitle')}
				className="flex flex-col gap-3"
				onSubmit={(e) => {
					e.preventDefault()
					props.onEditorSubmit()
				}}
			>
				<UserField props={props} editor={editor} />
				<SelectInput
					label={t('assignments.fields.assignmentType')}
					value={editor.values.assignmentType}
					emptyLabel=""
					options={Object.values(OrganizationAssignmentType).map((v) => ({ id: v, label: t(`assignmentType.${v}`) }))}
					error={editor.errors.assignmentType}
					onChange={(v) => props.onEditorChange('assignmentType', v as OrganizationAssignmentType)}
				/>
				<WindowFields props={props} editor={editor} />
				<ErrorText message={editor.formError} />
				<div className="flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{t('assignments.save')}
					</Button>
					<Button variant="muted" onPress={props.onEditorClose}>
						{t('assignments.cancel')}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}

function AssignmentRow({ props, row }: { props: OrgAssignmentsViewProps; row: OrgAssignmentRowView }): ReactElement {
	const { t } = props
	return (
		<li className="flex items-center gap-2 text-ds-sm" aria-label={row.userLabel}>
			<span>{row.userLabel}</span>
			<Badge size="sm" variant={row.assignmentType === OrganizationAssignmentType.MANAGER ? 'secondary' : 'muted'}>
				{t(`assignmentType.${row.assignmentType}`)}
			</Badge>
			{row.isPrimary ? (
				<Badge size="sm" variant="primary">
					{t('assignments.primary')}
				</Badge>
			) : null}
			<span className="text-ds-text-muted">
				{row.validFrom} → {row.validTo ?? t('assignments.openEnded')}
			</span>
			<span className="ml-auto flex gap-2">
				{props.onMakePrimary && row.canMakePrimary ? (
					<Button size="sm" variant="muted" onPress={() => props.onMakePrimary?.(row.id)}>
						{t('assignments.makePrimary')}
					</Button>
				) : null}
				{props.onEnd && row.current ? (
					<Button size="sm" variant="danger" onPress={() => props.onEnd?.(row.id)}>
						{t('assignments.end')}
					</Button>
				) : null}
			</span>
		</li>
	)
}

function Body({ props }: { props: OrgAssignmentsViewProps }): ReactElement {
	const { t } = props
	if (props.state === 'loading') return <p aria-live="polite">{t('tree.loading')}</p>
	if (props.state === 'error') {
		return (
			<div role="alert" className="flex items-center gap-2 text-ds-sm">
				{t('assignments.error')}
				<Button size="sm" variant="muted" onPress={props.onRetry}>
					{t('tree.retry')}
				</Button>
			</div>
		)
	}
	if (props.rows.length === 0) return <p className="text-ds-sm text-ds-text-muted">{t('assignments.empty')}</p>
	return (
		<ul className="flex flex-col gap-2">
			{props.rows.map((row) => (
				<AssignmentRow key={row.id} props={props} row={row} />
			))}
		</ul>
	)
}

/** A unit's user assignments: state filter, list, assign dialog, end and make-primary (organism). */
export function OrgAssignmentsView(props: OrgAssignmentsViewProps): ReactElement {
	const { t } = props
	return (
		<section aria-label={t('assignments.title')} className="flex flex-col gap-2">
			<header className="flex items-center justify-between gap-2">
				<h3 className="text-ds-base font-semibold">{t('assignments.title')}</h3>
				{props.onAssign ? (
					<Button size="sm" onPress={props.onAssign}>
						{t('assignments.assign')}
					</Button>
				) : null}
			</header>
			<SelectInput
				label={t('assignments.stateLabel')}
				value={props.stateFilter}
				options={Object.values(OrganizationAssignmentStateFilter).map((v) => ({
					id: v,
					label: t(`assignments.state.${v}`),
				}))}
				onChange={(v) => props.onStateFilterChange(v as OrganizationAssignmentStateFilter)}
			/>
			<ErrorText message={props.rowError} />
			<Body props={props} />
			<AssignDialog props={props} />
			<ProductConfirmDialogView
				t={t}
				isOpen={props.ending !== null}
				title={t('assignments.endTitle')}
				body={t('assignments.endBody', { user: props.ending?.label ?? '' })}
				confirmLabel={t('assignments.confirm')}
				cancelLabel={t('assignments.cancel')}
				danger
				error={props.ending?.error}
				onConfirm={props.onConfirmEnd}
				onCancel={props.onCancelEnd}
			/>
		</section>
	)
}
